import React, { useEffect, useState } from 'react';
import {
  Container, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, IconButton, Button, Box, Alert, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormControl,
  InputLabel, Select, MenuItem
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import { authApi, swarmApi } from '../api';

function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialog, setDialog] = useState({ open: false, mode: 'create', user: null });
  const [form, setForm] = useState({ username: '', email: '', password: '', role: 'viewer', phone: '', assignedServices: [], companyId: '' });
  const [availableServices, setAvailableServices] = useState([]);
  const [companies, setCompanies] = useState([]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await authApi.listUsers();
      setUsers(res.data);
      setError('');
    } catch (err) {
      setError('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  useEffect(() => {
    swarmApi.listServices().then(res => {
      setAvailableServices(res.data.map(s => s.name));
    }).catch(() => {});
    authApi.listCompanies().then(res => setCompanies(res.data)).catch(() => {});
  }, []);

  const openCreate = () => {
    setForm({ username: '', email: '', password: '', role: 'viewer', phone: '', assignedServices: [] });
    setDialog({ open: true, mode: 'create', user: null });
  };

  const openEdit = (user) => {
    let svcList = [];
    try { svcList = typeof user.assignedServices === 'string' ? JSON.parse(user.assignedServices) : (user.assignedServices || []); } catch { svcList = []; }
    if (!Array.isArray(svcList)) svcList = [];
    setForm({ username: user.username, email: user.email, password: '', role: user.role, phone: user.phone || '', assignedServices: svcList, companyId: user.companyId || '' });
    setDialog({ open: true, mode: 'edit', user });
  };

  const handleSubmit = async () => {
    try {
      if (dialog.mode === 'create') {
        await authApi.register(form);
      } else {
        const data = {
          email: form.email,
          role: form.role,
          phone: form.phone,
          assignedServices: Array.isArray(form.assignedServices) ? JSON.stringify(form.assignedServices) : form.assignedServices,
          companyId: form.companyId ? parseInt(form.companyId, 10) : -1
        };
        if (form.password) data.password = form.password;
        await authApi.updateUser(dialog.user.id, data);
      }
      setDialog({ open: false, mode: 'create', user: null });
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.message || 'Operation failed');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await authApi.deleteUser(id);
        fetchUsers();
      } catch (err) {
        setError(err.response?.data?.message || 'Delete failed');
      }
    }
  };

  if (loading) {
    return <Container sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress /></Container>;
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" fontWeight="bold" sx={{ flexGrow: 1 }}>User Management</Typography>
        <Button startIcon={<AddIcon />} variant="contained" onClick={openCreate} sx={{ mr: 1 }}>Add User</Button>
        <Button startIcon={<RefreshIcon />} onClick={fetchUsers}>Refresh</Button>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Username</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Services</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Last Login</TableCell>
              <TableCell>Company</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id} hover>
                <TableCell>{u.username}</TableCell>
                <TableCell>{u.email}</TableCell>
                <TableCell><Chip label={u.role} size="small" color={u.role === 'admin' ? 'primary' : 'default'} /></TableCell>
                <TableCell>{u.phone || '-'}</TableCell>
                <TableCell>
                  {(() => {
                    let svcs = [];
                    try { svcs = typeof u.assignedServices === 'string' ? JSON.parse(u.assignedServices) : (u.assignedServices || []); } catch { svcs = []; }
                    if (!Array.isArray(svcs)) svcs = [];
                    return svcs.length > 0
                      ? svcs.map((s, i) => <Chip key={i} label={s} size="small" sx={{ mr: 0.5, mb: 0.5 }} />)
                      : <Typography variant="caption" color="text.secondary">All</Typography>;
                  })()}
                </TableCell>
                <TableCell>{new Date(u.createdAt).toLocaleDateString()}</TableCell>
                <TableCell>{u.lastLogin ? new Date(u.lastLogin).toLocaleString() : 'Never'}</TableCell>
                <TableCell>{u.company?.name || '—'}</TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => openEdit(u)}><EditIcon /></IconButton>
                  {u.username !== 'admin' && (
                    <IconButton size="small" color="error" onClick={() => handleDelete(u.id)}><DeleteIcon /></IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialog.open} onClose={() => setDialog({ open: false, mode: 'create', user: null })}>
        <DialogTitle>{dialog.mode === 'create' ? 'Create User' : 'Edit User'}</DialogTitle>
        <DialogContent>
          {dialog.mode === 'create' && (
            <TextField label="Username" fullWidth margin="normal" value={form.username}
              onChange={e => setForm(p => ({ ...p, username: e.target.value }))} />
          )}
          <TextField label="Email" fullWidth margin="normal" value={form.email}
            onChange={e => setForm(p => ({ ...p, email: e.target.value }))} />
          <TextField label={dialog.mode === 'create' ? 'Password' : 'New Password (optional)'}
            type="password" fullWidth margin="normal" value={form.password}
            onChange={e => setForm(p => ({ ...p, password: e.target.value }))} />
          <FormControl fullWidth margin="normal">
            <InputLabel>Role</InputLabel>
            <Select value={form.role} label="Role" onChange={e => setForm(p => ({ ...p, role: e.target.value }))}>
              <MenuItem value="admin">Admin</MenuItem>
              <MenuItem value="viewer">Viewer</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>Company</InputLabel>
            <Select value={form.companyId} label="Company" onChange={e => setForm(p => ({ ...p, companyId: e.target.value }))}>
              <MenuItem value="">— None —</MenuItem>
              {companies.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField label="Phone" fullWidth margin="normal" value={form.phone}
            onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} placeholder="+1234567890" />
          {availableServices.length > 0 && (
            <FormControl fullWidth margin="normal">
              <InputLabel>Assigned Services</InputLabel>
              <Select
                multiple
                value={form.assignedServices}
                label="Assigned Services"
                onChange={e => setForm(p => ({ ...p, assignedServices: e.target.value }))}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map(v => <Chip key={v} label={v} size="small" />)}
                  </Box>
                )}
              >
                {availableServices.map(svc => (
                  <MenuItem key={svc} value={svc}>{svc}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog({ open: false, mode: 'create', user: null })}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit}>
            {dialog.mode === 'create' ? 'Create' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default Users;
